import { HardHat, SlidersHorizontal } from 'lucide-react';

import { Button } from '../components/ui/Button.js';
import { ErrorState, RefreshingBar } from '../components/ui/Feedback.js';
import { AvisoNoMapa, ObrasMapa, ObrasMapaSkeleton } from '../features/obras/ObrasMapa.js';
import {
  BuscaDoRecorte,
  FiltrosDoRecorte,
  INITIAL_FILTERS,
  LimparFiltros,
  NO_FILTERS,
  countActiveFilters,
  restoreFilters,
  useObraQueryFilters,
  type ObraFilterState,
} from '../features/obras/Recorte.js';
import { useObrasNoMapa } from '../features/obras/api.js';
import { useAuth } from '../lib/auth.js';
import { useStoredState } from '../lib/storage.js';

/**
 * O Mapa de Obras: o acervo sobre o mapa, numa tela só dele (ADR 059) e de
 * tela cheia (design.md §8, Mapa de Obras).
 *
 * Em cima, uma barra só, compacta: o título, a busca, os filtros e a ordem. O
 * resto da área é do mapa — colado no trilho, na borda direita e no pé da
 * janela. A contagem por situação mora na legenda, sobre o mapa; uma faixa de
 * resumo sob os filtros repetiria a mesma conta.
 *
 * Os mesmos filtros da listagem, com o recorte guardado à parte — quem filtra
 * o mapa por uma secretaria não quer encontrar a lista filtrada, nem o
 * contrário. Sem página: o mapa mostra tudo o que os filtros deixam passar.
 */
export function MapaDeObrasPage() {
  const { user, organization } = useAuth();

  const [filtros, setFiltros] = useStoredState(
    `obras.mapa:${organization?.id ?? '-'}:${user?.id ?? '-'}`,
    INITIAL_FILTERS,
    restoreFilters,
  );

  const recorte = useObraQueryFilters(filtros);
  const { data, isPending, isFetching, isError, error, refetch } = useObrasNoMapa(recorte);
  const filtrando = countActiveFilters(filtros) > 0 || filtros.search.trim() !== '';

  function changeFilters(patch: Partial<ObraFilterState>): void {
    setFiltros((current) => ({ ...current, ...patch }));
  }

  function conteudo() {
    if (isError) {
      return (
        <div className="grid flex-1 place-items-center p-6">
          <ErrorState
            message={error instanceof Error ? error.message : 'Erro desconhecido.'}
            onRetry={() => void refetch()}
          />
        </div>
      );
    }

    if (isPending) return <ObrasMapaSkeleton />;

    /*
     * Recorte vazio não tira o mapa da tela: ele fica, velado, com o aviso por
     * cima — a página não muda de forma a cada filtro que esvazia o recorte.
     */
    const aviso =
      data.total > 0 ? undefined : filtrando ? (
        <AvisoNoMapa
          icone={SlidersHorizontal}
          titulo="Nenhuma obra com esses filtros"
          acao={
            countActiveFilters(filtros) > 0 ? (
              <Button onClick={() => changeFilters(NO_FILTERS)}>Limpar filtros</Button>
            ) : undefined
          }
        >
          Remova algum filtro ou revise os termos da busca.
        </AvisoNoMapa>
      ) : (
        <AvisoNoMapa icone={HardHat} titulo="Nenhuma obra cadastrada">
          As obras cadastradas aparecem aqui, no lugar onde ficam.
        </AvisoNoMapa>
      );

    return <ObrasMapa obras={data.items} aviso={aviso} />;
  }

  return (
    <div className="flex flex-1 flex-col lg:min-h-0">
      {/*
       * A barra de cima, compacta: a busca, os filtros e, na ponta direita, a
       * ordem. Sem título à vista — o trilho já marca a página, e a altura é do
       * mapa; o título fica para o leitor de tela. No computador é uma linha
       * só, e a busca cede espaço antes de qualquer filtro descer; se ainda
       * assim não couber, os filtros descem juntos. No celular, a busca numa
       * linha e os filtros numa faixa que rola de lado.
       *
       * O respiro maior à esquerda é o da alça do trilho, que fica sobre a
       * borda da página, na altura da barra.
       */}
      <header className="relative z-20 shrink-0 border-b border-line bg-surface">
        <h1 className="sr-only">Mapa de Obras</h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5 px-4 py-3 lg:pr-6 lg:pl-10">
          <BuscaDoRecorte
            state={filtros}
            onChange={changeFilters}
            className="min-w-[180px] flex-1 lg:max-w-[320px]"
          />
          <div className="flex w-full items-center gap-2 overflow-x-auto [scrollbar-width:none] lg:w-auto lg:flex-1 lg:overflow-visible [&::-webkit-scrollbar]:hidden">
            <FiltrosDoRecorte
              state={filtros}
              onChange={changeFilters}
              classeDaOrdem="w-44 shrink-0 lg:order-last lg:ml-auto"
            />
            <LimparFiltros state={filtros} onChange={changeFilters} />
          </div>
        </div>

        {/* Revalidação não desmonta o mapa: só a barra fina, no pé da barra de cima. */}
        <div className="absolute inset-x-0 bottom-0">
          <RefreshingBar active={isFetching && !isPending} />
        </div>
      </header>

      <div className="relative flex flex-1 flex-col lg:min-h-0">{conteudo()}</div>
    </div>
  );
}
